import { Command, Option } from 'commander';
import { init } from './commands/init.js';
import { isInitWizardReturnToHomeError, supportFiles } from './commands/init.js';
import { login } from './commands/login.js';
import { logout } from './commands/logout.js';
import { scaffold } from './commands/scaffold.js';
import { initChat } from './commands/init-chat.js';
import { pack } from './commands/pack.js';
import { unpack, listPacked } from './commands/unpack.js';
import { deploy } from './commands/deploy.js';
import { restoreTuiTerminal, runHomeTui } from './tui/home.js';
import { skill } from './commands/skill.js';
import { currentModels } from './commands/current-models.js';
import { availableModels } from './commands/available-models.js';
import { gitCredential } from './commands/git-credential.js';
import { gitLogin } from './commands/git-login.js';
import { registerDiagCommand } from './commands/diag.js';

// Injected at build time by tsup (see tsup.config.ts)
declare const __CLI_VERSION__: string;
const INIT_TUI_ENV_VAR = 'STANDARDAGENTS_INIT_TUI';

async function settleStdinForInitHandoff(stdin: NodeJS.ReadStream = process.stdin): Promise<void> {
  if (!stdin.isTTY) return;

  for (const eventName of ['data', 'readable', 'keypress'] as const) {
    for (const listener of stdin.listeners(eventName)) {
      stdin.removeListener(eventName, listener as (...args: any[]) => void);
    }
  }

  if (stdin.isRaw) {
    stdin.setRawMode(false);
  }
  stdin.pause();
  while (stdin.read() !== null) {
    // Drain buffered key events from the home screen before child process handoff.
  }
  await new Promise<void>((resolve) => setTimeout(resolve, 30));
  while (stdin.read() !== null) {
    // One more pass after Ink teardown settles.
  }
}

const program = new Command();

program
  .name('agents')
  .description('CLI tool for Standard Agents / AgentBuilder')
  .version(__CLI_VERSION__);

program
  .command('init [project-name]')
  .description('Create a new Standard Agents project')
  .option('-y, --yes', 'Skip prompts and use defaults')
  .option('--local', 'Use local project scaffolding instead of browser platform onboarding')
  .option('--cli', 'Use classic line-by-line CLI prompts instead of the TUI wizard')
  .option('--api-url <url>', 'Standard Agents platform API URL')
  .option('--endpoint <url>', 'Platform API endpoint for auth/bootstrap exchange')
  .option('--package-manager <manager>', 'Package manager for generated project: npm, pnpm, yarn, or bun')
  .option('--template <template>', 'Vite template to use', 'vanilla-ts')
  .option('--workspace', 'Install @standardagents packages as workspace:* (local pnpm workspace development)')
  .option('--no-dev', 'Clone and configure the project without starting the dev server')
  .addOption(new Option('--bootstrap <code>', 'Exchange a web onboarding bootstrap code'))
  .action(init);

program
  .command('login')
  .description('Authenticate this machine (or use --bootstrap for project bootstrap auth)')
  .option('--bootstrap <code>', 'Bootstrap code from web onboarding')
  .option('--endpoint <url>', 'Platform API endpoint (default: https://api.standardagents.ai)')
  .option('--no-open', 'Do not auto-open browser URL')
  .action(login);

program
  .command('logout')
  .description('Clear stored StandardAgents auth and local org-scoped project auth')
  .option('--org <org-id-or-slug>', 'Org ID or slug to logout (defaults to active org)')
  .option('--endpoint <url>', 'Platform API endpoint (default: https://api.standardagents.ai)')
  .action(logout);

program
  .command('scaffold')
  .description('Add Standard Agents to an existing Vite project')
  .option('--force', 'Overwrite existing configuration')
  .action(scaffold);

program
  .command('init-chat [project-name]')
  .description('Scaffold a frontend chat application')
  .option('-y, --yes', 'Skip prompts and use defaults')
  .option('--framework <framework>', 'Framework to use (vite or nextjs)')
  .action(initChat);

program
  .command('pack [agent-name]')
  .description('Pack an agent with all its dependencies')
  .option('--output <dir>', 'Output directory (default: agents/packed)')
  .option('--npm', 'Generate npm-ready package structure')
  .option('--version <version>', 'Package version (default: 1.0.0)')
  .option('--no-remove', 'Keep original files after packing')
  .option('--no-interactive', 'Skip interactive item mode selector')
  .action(pack);

program
  .command('unpack [package]')
  .description('Unpack a packed agent to the local agents directory')
  .option('--no-signatures', 'Remove package signatures (fully editable)')
  .action(unpack);

program
  .command('list-packed')
  .description('List all discovered packed agents')
  .action(listPacked);

program
  .command('deploy')
  .description('Push the current branch to the Standard Agents Artifacts remote')
  .action(deploy);

program
  .command('git-credential <operation>')
  .description('Git credential helper for Standard Agents platform repositories')
  .option('--endpoint <url>', 'Platform API endpoint')
  .action(gitCredential);

program
  .command('git-login')
  .description('Authenticate Git for Standard Agents platform repositories')
  .option('--bootstrap <code>', 'Bootstrap code from web onboarding')
  .option('--endpoint <url>', 'Platform API endpoint (default: https://api.standardagents.ai)')
  .option('--helper-command <command>', 'Credential helper command to add to global git config')
  .option('--no-config', 'Store Git auth but do not update global git config')
  .option('--no-open', 'Do not auto-open browser URL')
  .action(gitLogin);

program
  .command('support-files')
  .description('Write generated local support files for a Standard Agents platform repository')
  .option('--project-name <name>', 'Project name to use in the generated package manifest')
  .option('--package-manager <manager>', 'Package manager: npm, pnpm, yarn, or bun')
  .action(supportFiles);

program
  .command('skill')
  .description('Install the bundled AgentBuilder guidance into a supported coding agent')
  .option('--agent <target>', 'Install target: codex or claude')
  .option('--dir <path>', 'Install directory override for the selected target')
  .option('--force', 'Overwrite an existing installed skill')
  .action(skill);

program
  .command('current-models')
  .description('Print the live curated model guide from standardagentspec.com')
  .option('--url <url>', 'Override the models markdown URL')
  .action(currentModels);

program
  .command('available-models')
  .description('List every available model for one configured provider in the current project')
  .option('--provider <provider>', 'Provider to query, for example cerebras or openai')
  .action(availableModels);

registerDiagCommand(program);

async function run(): Promise<void> {
  const userArgs = process.argv.slice(2);
  const isInteractiveTerminal = Boolean(process.stdin.isTTY && process.stdout.isTTY);

  if (userArgs.length === 0 && isInteractiveTerminal) {
    while (true) {
      const selection = await runHomeTui();
      if (!selection) return;

      if (selection.preserveTuiScreen) {
        await settleStdinForInitHandoff();
        process.stdout.write('\x1b[2J\x1b[H');
        try {
          await init(undefined, { returnToHomeOnCancel: true });
        } catch (error) {
          restoreTuiTerminal();
          if (isInitWizardReturnToHomeError(error)) {
            continue;
          }
          throw error;
        }
        restoreTuiTerminal();
        return;
      }

      await program.parseAsync(selection.argv, { from: 'user' });
      return;
    }
  }

  await program.parseAsync(process.argv);
}

run().catch((error) => {
  console.error(error instanceof Error ? error.message : String(error));
  process.exit(1);
});
